import { FONT_OPTIONS, PRESET_COLORS } from './constants';
import type {
    Alignments,
    DividerCustomizations,
    FooterCustomizations,
    HeaderCustomizations,
    TableCustomizations,
} from '@/types/report';

/**
 * Current-value summaries shown on each collapsed section row of the Personalizar
 * modal, so the whole configuration is readable without opening six accordions.
 *
 * Everything here is a pure string builder over the customizations object — no
 * React, no DOM — which is what lets it be unit tested (vitest only collects
 * `src/**\/*.test.ts` in a node environment).
 */

const SEPARATOR = ' · ';

/** Shown by the two sections that act on blocks the laudo may not contain. */
export const NO_BLOCKS_LABEL = 'nenhum neste laudo';

const ALIGNMENT_LABELS: Record<Alignments, string> = {
    left: 'esquerda',
    center: 'centro',
    right: 'direita',
};

const join = (parts: Array<string | null | false | undefined>): string =>
    parts.filter((part): part is string => !!part).join(SEPARATOR);

/** Reads as a phrase, unlike the bare noun the header row uses. */
const TEXT_ALIGNMENT_LABELS: Record<Alignments, string> = {
    left: 'Texto à esquerda',
    center: 'Texto centralizado',
    right: 'Texto à direita',
};

const alignmentLabel = (alignment: Alignments | undefined, fallback: Alignments): string =>
    ALIGNMENT_LABELS[alignment ?? fallback];

const textAlignmentLabel = (alignment: Alignments | undefined, fallback: Alignments): string =>
    TEXT_ALIGNMENT_LABELS[alignment ?? fallback];

/** "Marinho" when the pair matches a preset, the hex when it doesn't. */
export function summarizeColors(primary?: string, secondary?: string): string {
    if (!primary && !secondary) return 'Padrão';

    const preset = PRESET_COLORS.find(
        (candidate) =>
            candidate.primary.toLowerCase() === primary?.toLowerCase() &&
            candidate.secondary.toLowerCase() === secondary?.toLowerCase(),
    );
    if (preset) return preset.name;

    return (primary ?? secondary ?? 'Padrão').toUpperCase();
}

/** "Georgia · serifada" — the family name without the redundant parenthetical. */
export function summarizeFont(family: string | undefined): string {
    const font = FONT_OPTIONS.find((option) => option.value === family);
    if (!font) return family || 'Padrão';

    const name = font.name.replace(/\s*\(.*\)$/, '');
    return join([name, font.category === 'serif' ? 'serifada' : 'moderna']);
}

/** Logo and letterhead live in `customizations.header`; only their home moved. */
export function summarizeBrandAssets(header: HeaderCustomizations | undefined): string {
    const parts: string[] = [];

    if (header?.show_logo) parts.push(header.logo_url ? 'Logo' : 'Logo sem arquivo');
    if (header?.show_letterhead) {
        parts.push(header.letterhead_url ? 'Papel timbrado' : 'Papel timbrado sem arquivo');
    }

    return parts.length ? join(parts) : 'Nenhum';
}

export function summarizeHeader(
    header: HeaderCustomizations | undefined,
    options: { hasCrp: boolean },
): string {
    const parts: string[] = [];

    if (header?.show_psychologist_name !== false) parts.push('Nome');
    if (header?.show_crp_number !== false && options.hasCrp) parts.push('CRP');
    if (header?.description?.showing) parts.push('Descrição');

    const alignment = alignmentLabel(header?.alignment, 'left');
    return parts.length ? join([...parts, alignment]) : join(['Sem dados', alignment]);
}

/**
 * Phone and Instagram only reach the page when the psychologist actually has
 * them (`ReportFooter.tsx:35,46`), so the summary counts availability too.
 */
export function summarizeFooter(
    footer: FooterCustomizations | undefined,
    options: { hasPhone: boolean; hasInstagram: boolean },
): string {
    const parts = join([
        footer?.phone_number_visible && options.hasPhone && 'Telefone',
        footer?.psychologist_name_visible && 'Nome',
        footer?.email_visible && 'E-mail',
        footer?.instagram_visible && options.hasInstagram && 'Instagram',
    ]);

    return parts || 'Nenhum campo';
}

export function summarizeTable(table: TableCustomizations | undefined): string {
    // A coloração é o padrão, então só o desvio merece uma linha no resumo.
    return join([
        textAlignmentLabel(table?.text_alignment, 'center'),
        table?.color_classifications === false && 'sem cor nas classificações',
    ]);
}

/**
 * Resolves the `background_color` sentinel exactly as every renderer does
 * (`TestDivider.tsx:28`): a missing value means the report's own colour, an
 * empty string means transparent, anything else is a literal colour. Returns
 * `null` for transparent so callers can ask `=== null` instead of repeating
 * the `?? 'primary'` dance.
 */
export function resolveDividerBackground(
    divider: DividerCustomizations | undefined,
): string | null {
    return (divider?.background_color ?? 'primary') || null;
}

export function summarizeDivider(divider: DividerCustomizations | undefined): string {
    const style = divider?.font_style;
    // Over a filled background the line is drawn in the report colour on top of
    // the report colour — invisible. Saying "sem linha" there names a setting
    // that changes nothing.
    const lineMatters = resolveDividerBackground(divider) === null;
    return join([
        textAlignmentLabel(divider?.alignment, 'center'),
        style === 'bold' ? 'negrito' : style === 'italic' ? 'itálico' : null,
        lineMatters && divider?.show_line === false ? 'sem linha' : null,
    ]);
}
